import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Row, Col } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { useSelector, useDispatch } from 'react-redux'
import { OldEditEvaluationConfig } from 'models/oldEvaluation.model'
import { QuestionRating } from 'models/question.model'
import InputLabel from 'components/ui/forms/field/input-label/InputLabel'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import SwitchCustom from 'components/ui/forms/field/switch-custom/SwitchCustom'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import * as fromQuestionActions from 'store/actions/questions.action'
import { IMainState } from 'store/states'
import EmptyModal from 'components/ui/modals/emptyModal/EmptyModal'
import styles from './EvaluationConfig.module.scss'

interface IEvaluationQuestions {
  onChange: (config: OldEditEvaluationConfig) => void
  optionsData: OldEditEvaluationConfig
  editedQuestions: QuestionRating[]
  onQuestionWeightChange: (questions: QuestionRating[]) => void
  instanceId: number
}

const EvaluationConfig: React.FC<IEvaluationQuestions> = ({
  optionsData,
  onChange,
  editedQuestions,
  onQuestionWeightChange,
  instanceId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [questionsWeight, setQuestionsWeight] = useState<
    Record<number, number | undefined | null>
  >({})

  const { list } = useSelector(({ questions }: IMainState) => questions)
  const [showHelpModal, setShowHelpModal] = useState(false)

  const questionsArray = useMemo(
    () =>
      list.map(
        ({ questionableId: id, name, points }): QuestionRating => ({
          id,
          text: name,
          weight: points,
        })
      ),
    [list]
  )

  useEffect(() => {
    dispatch({ type: fromQuestionActions.GET_QUESTIONS_LIST, instanceId })
  }, [dispatch, instanceId])

  const handleSetWeight = (weight: number, index: number) => {
    if (weight >= 0 && weight <= 1000) {
      const aux = editedQuestions.map((question) => ({ ...question }))
      const exist = editedQuestions.findIndex(
        (question) => question.id === questionsArray[index].id
      )

      if (exist >= 0) {
        aux[exist].weight = weight
        onQuestionWeightChange(aux)
      } else {
        const aux2 = { ...questionsArray[index], weight }
        onQuestionWeightChange(editedQuestions.concat(aux2))
      }
      onChange({ ...optionsData, editedQuestions: editedQuestions })
      setQuestionsWeight((prev) => ({ ...prev, [index]: weight }))
    }
  }

  useEffect(() => {
    const aux = new Array(questionsArray.length)
    questionsArray.map(({ weight }, index: number) => {
      aux[index] = weight || 0
    })
    setQuestionsWeight(aux)
  }, [questionsArray])

  const switchOptions = [
    t('administration.blocks.type.evaluation.switchOptions.yes'),
    t('administration.blocks.type.evaluation.switchOptions.no'),
  ]

  const handleChangeOptionNumber = (
    number: number,
    option: string,
    limit: number
  ) => {
    if (number >= 0 && (number <= limit || limit === 0))
      onChange({ ...optionsData, [option]: Number(number) })
  }

  const modalMessage = () => {
    return (
      <div className="modalContent">
        <p className="modalTitle">
          <b>
            {' '}
            {t(
              'administration.blocks.type.evaluation.helpers.howItWorksMessage.questionsWeight.title'
            )}
          </b>
        </p>
        <p>
          {t(
            'administration.blocks.type.evaluation.helpers.howItWorksMessage.questionsWeight.text'
          )}
        </p>
        <p className="modalTitle">
          <b>
            {t(
              'administration.blocks.type.evaluation.helpers.howItWorksMessage.pointsToApprove.title'
            )}
          </b>
        </p>
        <p>
          {t(
            'administration.blocks.type.evaluation.helpers.howItWorksMessage.pointsToApprove.text'
          )}
        </p>
        <p className="modalTitle">
          <b>
            {t(
              'administration.blocks.type.evaluation.helpers.howItWorksMessage.attemptsNumber.title'
            )}
          </b>
        </p>
        <p>
          {t(
            'administration.blocks.type.evaluation.helpers.howItWorksMessage.attemptsNumber.text'
          )}
        </p>
      </div>
    )
  }

  return (
    <Row className={styles.container}>
      <div>
        <h1 className={styles.title}>
          {t('administration.blocks.type.evaluation.titles.qualification')}
        </h1>
      </div>
      <div className={styles.questions}>
        <div className="d-flex">
          <h2 className={styles.subtitle}>
            {t('administration.blocks.type.evaluation.questionsWeight')}
          </h2>
          <a className={styles.helpers} onClick={() => setShowHelpModal(true)}>
            {t('administration.blocks.type.evaluation.helpers.howItWorks')}
          </a>
        </div>
        <div>
          <p className={styles.explanation}>
            {t('administration.blocks.type.evaluation.weightConfigExplanation')}
          </p>
        </div>
        <div>
          {questionsArray.length > 0 ? (
            questionsArray.map(({ id: questionId, text }, index) => (
              <div
                key={questionId + '-' + index}
                className={`d-flex justify-content-between align-items-center mt-3`}
              >
                <Col xs={10} className={`p-0`}>
                  <InputLabel
                    className="mb-0"
                    label={`${t(
                      'administration.blocks.type.evaluation.question.singular'
                    )} ${index + 1}`}
                  />
                  <div className={styles.questionText}> {text} </div>
                </Col>
                <Col className={`p-0 ${styles.rightSide}`}>
                  <TextInput
                    name={'weight' + index}
                    type={'number'}
                    value={questionsWeight[index] || 0}
                    label={''}
                    placeHolder={''}
                    onChange={(e) => {
                      const { value } = e.target
                      handleSetWeight(value ? parseInt(value) : 0, index)
                    }}
                    error={''}
                  />
                </Col>
              </div>
            ))
          ) : (
            <p className={`${styles.emptyList} m-0 mt-2`}>
              {t('administration.blocks.type.evaluation.emptyQuestionList')}
            </p>
          )}
        </div>
      </div>

      <div className={`${styles.optionsContainer} w-100`}>
        <div className={`${styles.evaluationOptions} mt-4`}>
          <h2>
            {t('administration.blocks.type.evaluation.evaluationOptions')}
          </h2>
          <CommonToolTip
            text={t(
              'administration.blocks.type.evaluation.tooltips.configOptions'
            )}
            position="right"
            className={styles.tabsTooltip}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              className={`ml-2 ${styles.infoIcon}`}
            />
          </CommonToolTip>
        </div>
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <Col xs={9} className={`p-0`}>
            <p className={styles.option}>
              {t('administration.blocks.type.evaluation.pointsToApprove')}
            </p>
          </Col>
          <Col className={`p-0 ${styles.rightSide}`}>
            <TextInput
              name={'approvalPoints'}
              type={'number'}
              value={optionsData.approvalPoints || 0}
              label={''}
              placeHolder={''}
              onChange={(e) => {
                const { value } = e.target
                handleChangeOptionNumber(
                  value ? parseInt(value) : 0,
                  'approvalPoints',
                  0
                )
              }}
              error={''}
            />
          </Col>
        </div>
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <Col xs={9} className={`p-0`}>
            <p className={styles.option}>
              {t('administration.blocks.type.evaluation.attemptsNumber')}
            </p>
          </Col>
          <Col className={`p-0 ${styles.rightSide}`}>
            <TextInput
              name={'maxRetries'}
              type={'number'}
              value={optionsData.maxRetries || 0}
              label={''}
              placeHolder={''}
              onChange={(e) => {
                const { value } = e.target
                handleChangeOptionNumber(
                  value ? parseInt(value) : 0,
                  'maxRetries',
                  100
                )
              }}
              disabled={false}
              error={''}
            />
          </Col>
        </div>
        <div className="mt-3 d-flex justify-content-between align-items-center">
          <Col xs={9} className={`p-0`}>
            <p className={styles.option}>
              {t('administration.blocks.type.evaluation.canCheckResult')}
            </p>
          </Col>
          <Col className={`p-0 ${styles.rightSide}`}>
            <SwitchCustom
              name={'showResults'}
              value={optionsData.showResults}
              label={''}
              onChange={(value: boolean) =>
                onChange({ ...optionsData, showResults: value })
              }
              options={switchOptions}
            />
          </Col>
        </div>

        <div className="mt-3 d-flex justify-content-between align-items-center">
          <Col xs={9} className={`p-0`}>
            <p className={styles.option}>
              {t('administration.blocks.type.evaluation.randomQuestionOrder')}
            </p>
          </Col>
          <Col className={`p-0 ${styles.rightSide}`}>
            <SwitchCustom
              className={styles.switch}
              name={'randomQuestions'}
              value={optionsData.randomQuestions}
              label={''}
              onChange={(value: boolean) =>
                onChange({ ...optionsData, randomQuestions: value })
              }
              options={switchOptions}
            />
          </Col>
        </div>
      </div>

      {showHelpModal && (
        <EmptyModal
          show={showHelpModal}
          title={t('administration.blocks.type.evaluation.helpers.howItWorks')}
          text={modalMessage()}
          onClose={() => setShowHelpModal(false)}
          showButtons={false}
        />
      )}
    </Row>
  )
}

export default EvaluationConfig
